import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTransferPatientsData } from '@/hooks/patient/useTransferPatientsData';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TransferPatientsList: React.FC = () => {
  const {
    sortedNotifications,
    isLoading
  } = useTransferPatientsData();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  // Calculate pagination values
  const { paginatedNotifications, totalPages, startIndex, endIndex } = useMemo(() => {
    const startIdx = (currentPage - 1) * recordsPerPage;
    const endIdx = startIdx + recordsPerPage;
    const paginated = sortedNotifications.slice(startIdx, endIdx);
    const totalPgs = Math.ceil(sortedNotifications.length / recordsPerPage);

    return {
      paginatedNotifications: paginated,
      totalPages: totalPgs,
      startIndex: startIdx,
      endIndex: endIdx
    };
  }, [sortedNotifications, currentPage, recordsPerPage]);

  // Handle pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleRecordsPerPageChange = (newSize: number) => {
    setRecordsPerPage(newSize);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading transfer patients...</span>
        </div>
      </div>
    );
  }

  if (sortedNotifications.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
          <Clock className="h-12 w-12 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-2">No Transfer Patients</h3>
        <p className="text-muted-foreground">There are currently no patients waiting for transfer acceptance.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">
          Transfer Patient List ({sortedNotifications.length})
        </h2>
        <p className="text-muted-foreground text-sm">
          Review and accept patient transfers assigned to you
        </p>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-4 gap-4 bg-muted/50 px-4 py-3 font-semibold text-sm border-b">
          <div>Patient Name</div>
          <div>Group Name</div>
          <div>Clinic Name</div>
          <div>Assigned VPE</div>
        </div>

        {/* Table Body */}
        <div className="divide-y">
          {paginatedNotifications.map((patient, index) => (
            <div
              key={patient.id}
              className={`grid grid-cols-4 gap-4 px-4 py-3 hover:bg-muted/30 transition-colors ${
                index % 2 === 0 ? 'bg-card' : 'bg-muted/10'
              }`}
            >
              <div className="font-medium">{patient.patient_name}</div>
              <div className="text-muted-foreground">{patient.group_name || 'No Group Assigned'}</div>
              <div className="text-muted-foreground">{patient.clinic_name}</div>
              <div className="text-muted-foreground">{patient.vpe_name || 'Not Assigned'}</div>
            </div>
          ))}
        </div>

        {/* Pagination Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-muted-foreground">Rows per page:</div>
            <Select value={recordsPerPage.toString()} onValueChange={(value) => handleRecordsPerPageChange(parseInt(value))}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-muted-foreground">
              {startIndex + 1}-{Math.min(endIndex, sortedNotifications.length)} of {sortedNotifications.length}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="flex items-center space-x-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="flex items-center space-x-1"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferPatientsList;
